/**
 * CR162 (updated): Stuck Run Types and Detection Logic
 * CR166: Added NEEDS_REVIEW for manual checkpoint gating
 * Provides distinct states for stuck detection:
 * - IDLE: No active steps, no failed AUTO steps (queue stuck)
 * - BLOCKED_BY_FAILURE: An AUTO step failed, blocking downstream
 * - TERMINAL: Run is complete or all AUTO steps are terminal
 * - HEALTHY: Run is actively processing
 * - NEEDS_REVIEW: Waiting at a manual review checkpoint (S6)
 */

export type StuckType = "IDLE" | "BLOCKED_BY_FAILURE" | "TERMINAL" | "HEALTHY" | "NEEDS_REVIEW";

/**
 * CR166: Derived display status for batch run header/list
 * These map to user-visible labels, not necessarily DB status values
 */
export type DerivedRunStatus = 
  | "CREATED" 
  | "RUNNING" 
  | "NEEDS_REVIEW" 
  | "COMPLETE" 
  | "FAILED" 
  | "CANCELLED" 
  | "PARTIAL";

export interface DerivedRunStatusInfo {
  status: DerivedRunStatus;
  label: string;
  helperText?: string;
}

// Manual review checkpoint steps (steps that require human action before continuing)
const MANUAL_REVIEW_STEPS = ["S6"];

export type RecoveryAction = "RESUME" | "RETRY_FAILED" | "SKIP_FAILED" | "NONE";

export interface BatchRunStep {
  id: string;
  step_key: string;
  step_name: string;
  status: string;
  mode: string;
  updated_at?: string;
  started_at?: string | null;
  error_message?: string | null;
}

export interface StepInfo {
  step_key: string;
  step_name: string;
  status: string;
  mode: string;
  error_message?: string | null;
}

export interface StuckRunDiagnostics {
  batch_run_id: string;
  run_status: string;
  stuck_type: StuckType;
  from_schedule: boolean;
  schedule_run_id?: string;
  schedule_queue_status?: string;
  sources_enabled: string[];
  step_counts: Record<string, number>;
  blocker_step: StepInfo | null;
  recovery_recommended_action: RecoveryAction;
  recovery_target_step: StepInfo | null;
  all_steps: Array<{
    step_key: string;
    status: string;
    mode: string;
  }>;
}

// Step order for determining prerequisites
// CR502: Added S1_5 after S1_SNIPPET (compat-only, but needs ordering for diagnostics)
const STEP_ORDER = ["S1", "S1_RENDER", "S1_OCR", "S1_AI", "S1_4", "S1_5", "S2", "S3", "S4", "S5", "S6", "S7", "S8"];

// Terminal success statuses (step completed successfully)
const TERMINAL_SUCCESS_STATUSES = ["COMPLETE", "PARTIAL", "SKIPPED"];

// Terminal statuses (step is done, regardless of outcome)
const TERMINAL_STATUSES = ["COMPLETE", "PARTIAL", "FAILED", "SKIPPED", "CANCELLED"];

// Active statuses (step is in progress)
const ACTIVE_STATUSES = ["ENQUEUED", "RUNNING"];

/**
 * Get the order index of a step key
 */
function getStepOrderIndex(stepKey: string): number {
  const index = STEP_ORDER.indexOf(stepKey);
  return index === -1 ? 999 : index;
}

/**
 * Sort steps by pipeline order
 */
export function sortStepsByOrder(steps: BatchRunStep[]): BatchRunStep[] {
  return [...steps].sort((a, b) => getStepOrderIndex(a.step_key) - getStepOrderIndex(b.step_key));
}

/**
 * Find the earliest FAILED AUTO step in pipeline order
 */
export function getEarliestFailedAutoStep(steps: BatchRunStep[]): BatchRunStep | null {
  const sortedSteps = sortStepsByOrder(steps);
  return sortedSteps.find((s) => s.mode === "AUTO" && s.status === "FAILED") ?? null;
}

/**
 * Find the earliest NOT_STARTED AUTO step that has all prerequisites met
 */
export function getNextResumableAutoStep(
  steps: BatchRunStep[],
  sourcesEnabled: string[]
): BatchRunStep | null {
  const sortedSteps = sortStepsByOrder(steps);
  
  // Check if there are any failed AUTO steps (which would block resumption)
  const failedAutoStep = getEarliestFailedAutoStep(steps);
  if (failedAutoStep) {
    return null; // Cannot resume when blocked by failure
  }

  // Find first NOT_STARTED AUTO step
  for (const step of sortedSteps) {
    if (step.mode === "AUTO" && step.status === "NOT_STARTED") {
      // Check if all AUTO prerequisites are terminal-success
      const stepIndex = getStepOrderIndex(step.step_key);
      const prerequisitesMet = sortedSteps
        .filter((s) => s.mode === "AUTO" && getStepOrderIndex(s.step_key) < stepIndex)
        .every((s) => TERMINAL_SUCCESS_STATUSES.includes(s.status));
      
      if (prerequisitesMet) {
        return step;
      }
    }
  }

  return null;
}

/**
 * Determine the stuck type for a batch run
 */
export function determineStuckType(runStatus: string, steps: BatchRunStep[]): StuckType {
  // Terminal run statuses
  if (["COMPLETE", "FAILED", "CANCELLED", "PARTIAL"].includes(runStatus)) {
    return "TERMINAL";
  }

  // Check for active steps
  const hasActiveStep = steps.some((s) => ACTIVE_STATUSES.includes(s.status));
  if (hasActiveStep) {
    return "HEALTHY";
  }

  // Check for failed AUTO steps with downstream NOT_STARTED AUTO steps
  const sortedSteps = sortStepsByOrder(steps);
  const failedAutoStep = getEarliestFailedAutoStep(steps);
  
  if (failedAutoStep) {
    // Check if there are downstream AUTO steps that are NOT_STARTED
    const failedIndex = getStepOrderIndex(failedAutoStep.step_key);
    const hasDownstreamAutoSteps = sortedSteps.some(
      (s) =>
        s.mode === "AUTO" &&
        s.status === "NOT_STARTED" &&
        getStepOrderIndex(s.step_key) > failedIndex
    );
    
    if (hasDownstreamAutoSteps) {
      return "BLOCKED_BY_FAILURE";
    }
    // If no downstream AUTO steps, run is terminal (all AUTO work done)
    return "TERMINAL";
  }

  // CR166: Check if waiting at a manual review checkpoint
  // This happens when all AUTO steps before the review step are complete,
  // and the review step (S6) is NOT_STARTED or MANUAL mode
  const reviewStepInfo = isWaitingAtManualReviewCheckpoint(steps);
  if (reviewStepInfo.isWaiting) {
    return "NEEDS_REVIEW";
  }

  // Check if all AUTO steps are terminal
  const autoSteps = steps.filter((s) => s.mode === "AUTO");
  if (autoSteps.length > 0 && autoSteps.every((s) => TERMINAL_STATUSES.includes(s.status))) {
    return "TERMINAL";
  }

  // No active steps, no failed AUTO steps, but run is RUNNING
  if (runStatus === "RUNNING" && steps.length > 0) {
    return "IDLE";
  }

  return "HEALTHY";
}

/**
 * CR166: Check if the run is waiting at a manual review checkpoint (like S6)
 */
export function isWaitingAtManualReviewCheckpoint(steps: BatchRunStep[]): { 
  isWaiting: boolean; 
  checkpointStep?: string; 
} {
  const sortedSteps = sortStepsByOrder(steps);
  
  for (const reviewStepKey of MANUAL_REVIEW_STEPS) {
    const reviewStep = sortedSteps.find((s) => s.step_key === reviewStepKey);
    if (!reviewStep) continue;
    
    // Review step must be NOT_STARTED or have MANUAL mode
    const isReviewPending = reviewStep.status === "NOT_STARTED" || reviewStep.mode === "MANUAL";
    if (!isReviewPending) continue;
    
    // All AUTO steps before this review step must be terminal-success
    const reviewIndex = getStepOrderIndex(reviewStepKey);
    const prerequisiteAutoSteps = sortedSteps.filter(
      (s) => s.mode === "AUTO" && getStepOrderIndex(s.step_key) < reviewIndex
    );
    
    // Must have at least some prerequisite steps that completed
    const allPrereqsComplete = prerequisiteAutoSteps.length > 0 &&
      prerequisiteAutoSteps.every((s) => TERMINAL_SUCCESS_STATUSES.includes(s.status));
    
    // No failed steps
    const hasAnyFailure = sortedSteps.some((s) => s.status === "FAILED");
    
    if (allPrereqsComplete && !hasAnyFailure) {
      return { isWaiting: true, checkpointStep: reviewStepKey };
    }
  }
  
  return { isWaiting: false };
}

/**
 * CR166: Derive the display status for a batch run based on steps and run state
 * This provides user-friendly status labels that accurately reflect the run's state
 */
export function deriveBatchRunDisplayStatus(
  runStatus: string,
  steps: BatchRunStep[],
  errorCount: number = 0
): DerivedRunStatusInfo {
  // 1. FAILED if any step failed OR errors present
  const hasFailedStep = steps.some((s) => s.status === "FAILED");
  if (hasFailedStep || errorCount > 0) {
    return {
      status: "FAILED",
      label: "Failed",
      helperText: hasFailedStep ? "A step has failed" : "Errors occurred during processing",
    };
  }
  
  // 2. Respect terminal DB statuses
  if (runStatus === "CANCELLED") {
    return { status: "CANCELLED", label: "Cancelled" };
  }
  if (runStatus === "COMPLETE") {
    return { status: "COMPLETE", label: "Complete" };
  }
  if (runStatus === "PARTIAL") {
    return { status: "PARTIAL", label: "Partial", helperText: "Completed with some issues" };
  }
  
  // 3. NEEDS_REVIEW if waiting at manual checkpoint (S6)
  const reviewCheckpoint = isWaitingAtManualReviewCheckpoint(steps);
  if (reviewCheckpoint.isWaiting) {
    return {
      status: "NEEDS_REVIEW",
      label: "Needs Review",
      helperText: `Awaiting review at ${reviewCheckpoint.checkpointStep} to continue`,
    };
  }
  
  // 4. RUNNING if any step in progress
  const hasActiveStep = steps.some((s) => ACTIVE_STATUSES.includes(s.status));
  if (hasActiveStep || runStatus === "RUNNING") {
    return { status: "RUNNING", label: "Running" };
  }
  
  // 5. CREATED if not started
  if (runStatus === "CREATED") {
    return { status: "CREATED", label: "Created" };
  }
  
  // Default fallback
  return { status: runStatus as DerivedRunStatus, label: runStatus };
}

/**
 * Determine the recommended recovery action based on stuck type
 */
export function getRecommendedRecoveryAction(
  stuckType: StuckType,
  steps: BatchRunStep[],
  sourcesEnabled: string[]
): { action: RecoveryAction; targetStep: BatchRunStep | null } {
  switch (stuckType) {
    case "IDLE": {
      const nextStep = getNextResumableAutoStep(steps, sourcesEnabled);
      return {
        action: nextStep ? "RESUME" : "NONE",
        targetStep: nextStep,
      };
    }
    case "BLOCKED_BY_FAILURE": {
      const failedStep = getEarliestFailedAutoStep(steps);
      return {
        action: failedStep ? "RETRY_FAILED" : "NONE",
        targetStep: failedStep,
      };
    }
    case "TERMINAL":
    case "HEALTHY":
    default:
      return { action: "NONE", targetStep: null };
  }
}

/**
 * Build full diagnostics for a stuck run
 */
export function buildStuckRunDiagnostics(
  batchRunId: string,
  runStatus: string,
  steps: BatchRunStep[],
  sourcesEnabled: string[],
  configJson: Record<string, unknown>
): StuckRunDiagnostics {
  const stuckType = determineStuckType(runStatus, steps);
  const failedAutoStep = getEarliestFailedAutoStep(steps);
  const { action, targetStep } = getRecommendedRecoveryAction(stuckType, steps, sourcesEnabled);

  const stepCounts = steps.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return {
    batch_run_id: batchRunId,
    run_status: runStatus,
    stuck_type: stuckType,
    from_schedule: configJson?.from_schedule === true,
    schedule_run_id: configJson?.schedule_run_id as string | undefined,
    schedule_queue_status: configJson?.schedule_queue_status as string | undefined,
    sources_enabled: sourcesEnabled,
    step_counts: stepCounts,
    blocker_step: failedAutoStep
      ? {
          step_key: failedAutoStep.step_key,
          step_name: failedAutoStep.step_name,
          status: failedAutoStep.status,
          mode: failedAutoStep.mode,
          error_message: failedAutoStep.error_message,
        }
      : null,
    recovery_recommended_action: action,
    recovery_target_step: targetStep
      ? {
          step_key: targetStep.step_key,
          step_name: targetStep.step_name,
          status: targetStep.status,
          mode: targetStep.mode,
        }
      : null,
    all_steps: steps.map((s) => ({
      step_key: s.step_key,
      status: s.status,
      mode: s.mode,
    })),
  };
}

/**
 * Legacy compatibility: Check if run needs attention (IDLE or BLOCKED_BY_FAILURE)
 */
export function runNeedsAttention(runStatus: string, steps: BatchRunStep[]): boolean {
  const stuckType = determineStuckType(runStatus, steps);
  return stuckType === "IDLE" || stuckType === "BLOCKED_BY_FAILURE";
}
